import { create } from 'zustand';

import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import type {
  CreatePaymentBody,
  CreatePaymentResponse,
  CreateTransactionBody,
  Transaction,
  TransactionCategory,
  TransactionStatus,
  TransactionType,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import { downloadBlob } from '@/utils/download-file-from-link.ts';
import { toast } from '@/utils/toast.ts';

import {
  createTransactionCategory,
  deleteTransactionCategory,
  getTransactionsCategories,
  updateTransactionCategory,
} from './transactions.api.ts';

type TransactionQueryParams = {
  from?: string;
  to?: string;
  type?: TransactionType;
  status?: TransactionStatus;
};

type TransactionsStore = {
  transactions: Transaction[];
  transaction: Transaction | null;
  transactionCategories: TransactionCategory[];
  getServiceProviderTransactions: () => Promise<void>;
  getAllTransactions: (params?: TransactionQueryParams) => Promise<void>;
  getTransactionById: (id: string) => Promise<void>;
  getServiceProviderTransactionsById: (id: string) => Promise<void>;
  getTransactionsByInvoiceId: (invoiceId: string) => Promise<Transaction[]>;
  getTransactionsCategories: () => Promise<void>;
  exportTransactions: (format?: 'csv' | 'pdf') => Promise<void>;
  createTransaction: (body: CreateTransactionBody) => Promise<void>;
  createPayment: (body: CreatePaymentBody) => Promise<string | null>;
  createTransactionCategory: (
    title: string,
    color: string,
    icon?: string,
  ) => Promise<TransactionCategory>;
  updateTransactionCategory: (
    categoryId: string,
    title: string,
    color: string,
    icon?: string,
  ) => Promise<unknown>;
  /** `targetCategoryId` takes over the deleted category's transactions and budgets; defaults to "Other". */
  deleteTransactionCategory: (categoryId: string, targetCategoryId?: string) => Promise<unknown>;
  updateTransaction: (id: string, body: CreateTransactionBody) => Promise<void>;
  setTransactionStatus: (id: string, status: TransactionStatus) => Promise<void>;
  removeTransaction: (id: string) => Promise<void>;
  loading: boolean;
  exportLoading: boolean;
  error: string | null;
};

export const useTransactionsStore = create<TransactionsStore>((set, get) => ({
  transactions: [],
  transaction: null,
  transactionCategories: [],
  loading: false,
  exportLoading: false,
  error: null,
  getServiceProviderTransactions: async () => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest<{ items: Transaction[]; count: number }>(
        API.EXPENSES,
        '/transaction?limit=1000&page=1&service_provider_view=true',
      );
      set({ transactions: response.data.items || [], loading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Couldn't load the transactions. Try again";
      set({ error: message, loading: false });
    }
  },
  getAllTransactions: async (params) => {
    set({ loading: true, error: null });
    try {
      const queryParams = new URLSearchParams({ limit: '1000', page: '1' });
      if (params?.from) queryParams.append('from', params.from);
      if (params?.to) queryParams.append('to', params.to);
      if (params?.type) queryParams.append('type', params.type);
      if (params?.status) queryParams.append('status', params.status);

      const response = await getRequest<{ items: Transaction[]; count: number }>(
        API.EXPENSES,
        `/transaction?${queryParams.toString()}`,
      );
      set({ transactions: response.data.items || [], loading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Couldn't load the transactions. Try again";
      set({ error: message, loading: false });
    }
  },
  getTransactionById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest<Transaction>(API.EXPENSES, `/transaction/${id}`);
      set({ transaction: response.data, loading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Couldn't load the transaction. Try again";
      set({ error: message, loading: false });
    }
  },
  getServiceProviderTransactionsById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest<Transaction>(
        API.EXPENSES,
        `/transaction/${id}?service_provider_view=true`,
      );
      set({ transaction: response.data, loading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Couldn't load the transaction. Try again";
      set({ error: message, loading: false });
    }
  },
  getTransactionsByInvoiceId: async (invoiceId: string) => {
    try {
      const response = await getRequest<{ items: Transaction[]; count: number }>(
        API.EXPENSES,
        `/transaction?limit=1000&page=1&invoice_id=${invoiceId}`,
      );
      return response.data.items || [];
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Couldn't load the transactions. Try again";
      set({ error: message });
      return [];
    }
  },
  exportTransactions: async (format = 'csv') => {
    if (get().exportLoading) return;
    set({ error: null, exportLoading: true });
    try {
      const endpoint = format === 'pdf' ? `/transaction/export/pdf` : `/transaction/export`;
      const blob = await getRequestBlob(API.EXPENSES, endpoint);
      const filename = `transactions-${new Date().toISOString().slice(0, 10)}.${format}`;
      await downloadBlob(blob, filename);

      toast.success('Transactions exported');
    } catch {
      toast.error('Couldn’t export the transactions. Please try again.');
    } finally {
      set({ exportLoading: false });
    }
  },
  createTransaction: async (body) => {
    set({ error: null });
    try {
      const response = await postRequest<Transaction, typeof body>(
        API.EXPENSES,
        '/transaction',
        body,
      );
      set((state) => ({
        transactions: [...state.transactions, response.data],
      }));

      toast.success('Transaction created');
      void get().getAllTransactions();
    } catch {
      toast.error('Couldn’t create the transaction. Please check the details.');
    }
  },
  createPayment: async (body) => {
    try {
      const res = await postRequest<CreatePaymentResponse, CreatePaymentBody>(
        API.PEOPLE,
        '/payments',
        body,
      );
      toast.success('Payment link created');
      void get().getAllTransactions(); // backend already inserted the incoming row
      return res.data.url;
    } catch {
      toast.error('Couldn’t create the payment. Please check the details.');
      return null;
    }
  },
  updateTransaction: async (id, body) => {
    set({ error: null });
    try {
      const response = await putRequest<Transaction, typeof body>(
        API.EXPENSES,
        `/transaction/${id}`,
        body,
      );
      set((state) => ({
        transactions: state.transactions.map((t) => (t.id === id ? response.data : t)),
        transaction: state.transaction?.id === id ? response.data : state.transaction,
      }));

      toast.success('Transaction updated');
      void get().getAllTransactions();
    } catch {
      toast.error('Couldn’t update the transaction. Please try again.');
    }
  },
  setTransactionStatus: async (id: string, status: TransactionStatus) => {
    set({ error: null });
    try {
      // Status has its own route: wallet registers PATCH /transaction/:id/status, and
      // plain PATCH /transaction/:id is not a route at all, so it 404s. The handler
      // answers 204, so the cached row is patched from what we sent, not from the body;
      // the refetch below brings back the server's finalized_at.
      await patchRequest<void, { status: TransactionStatus }>(
        API.EXPENSES,
        `/transaction/${id}/status`,
        { status },
      );
      set((state) => ({
        transactions: state.transactions.map((t) => (t.id === id ? { ...t, status } : t)),
        transaction:
          state.transaction?.id === id ? { ...state.transaction, status } : state.transaction,
      }));

      toast.success('Transaction status updated');
      void get().getAllTransactions();
    } catch {
      toast.error('Couldn’t update the transaction status. Please try again.');
    }
  },
  removeTransaction: async (id: string) => {
    set({ error: null });
    try {
      await deleteRequest(API.EXPENSES, `/transaction/${id}`);
      set((state) => ({
        transactions: state.transactions.filter((t) => t.id !== id),
        transaction: state.transaction?.id === id ? null : state.transaction,
      }));

      toast.success('Transaction deleted');
      void get().getAllTransactions();
    } catch {
      toast.error('Couldn’t delete the transaction. Please try again.');
    }
  },
  getTransactionsCategories: async () => {
    try {
      const categories = await getTransactionsCategories();
      set({ transactionCategories: categories || [] });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Couldn't load the categories. Try again";

      set({ error: message });
    }
  },
  createTransactionCategory: async (title, color, icon) => {
    try {
      const category = await createTransactionCategory(title, color, icon);
      const categories = await getTransactionsCategories();
      set({ transactionCategories: categories || [] });
      return category;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Couldn't create the category";
      set({ error: message });
      throw new Error(message);
    }
  },
  updateTransactionCategory: async (categoryId, title, color, icon) => {
    try {
      return await updateTransactionCategory(categoryId, title, color, icon).then(() =>
        get().getTransactionsCategories(),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Couldn't update the category";
      set({ error: message });
      toast.error('Couldn’t update the category. Please try again.');
      throw new Error(message);
    }
  },
  deleteTransactionCategory: async (categoryId: string, targetCategoryId?: string) => {
    try {
      return await deleteTransactionCategory(categoryId, targetCategoryId).then(() =>
        get().getTransactionsCategories(),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Couldn't delete the category";
      set({ error: message });
      toast.error('Couldn’t delete the category. Please try again.');
      throw new Error(message);
    }
  },
}));
